import { ApplicationConfig, makeStateKey, TransferState, provideZonelessChangeDetection, PLATFORM_ID, APP_INITIALIZER } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { provideClientHydration, withEventReplay, withHttpTransferCacheOptions } from '@angular/platform-browser';
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { InMemoryCache, ApolloLink } from '@apollo/client/core';
import { onError } from '@apollo/client/link/error';
import { HttpLink } from 'apollo-angular/http';
import { provideApollo, Apollo } from 'apollo-angular';
import { inject } from '@angular/core';
/* import { GoogleTagManagerModule } from 'angular-google-tag-manager'; */
import { provideRouter } from '@angular/router';
import { APP_ROUTES } from '../app/helper/contants';
import { GRAPH_CMS_TOKEN, AuthInterceptor } from '@ng-chronicles/shared/data-access';
import { environment } from '../environments/environment';

export const GRAPH_CMS_AUTH_KEY = makeStateKey<string>('GRAPH_CMS_AUTH');
export const APOLLO_STATE_KEY = makeStateKey<any>('apollo.state');

export const APP_BROWSER_CONFIG: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideClientHydration(
      withEventReplay(),
      withHttpTransferCacheOptions({
        includePostRequests: true,
      })
    ),
    provideRouter(APP_ROUTES),
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    {
      provide: GRAPH_CMS_TOKEN,
      useFactory: () => {
        const transferState = inject(TransferState);
        const auth = transferState.get(GRAPH_CMS_AUTH_KEY, null);
        if (auth) {
          return auth;
        }
        return (process?.env?.['GRAPH_CMS_AUTH']) || (environment as any).GRAPH_CMS_AUTH;
      },
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    provideApollo(() => {
      const httpLink = inject(HttpLink);
      const transferState = inject(TransferState);
      const isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

      const cache = new InMemoryCache();

      if (isBrowser) {
        const state = transferState.get(APOLLO_STATE_KEY, null);
        if (state) {
          cache.restore(state);
        }
      }

      const errorLink = onError(({ graphQLErrors, networkError }: any) => {
        if (graphQLErrors)
          graphQLErrors.forEach(({ message, locations, path }) => console.error(
            `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
          )
          );
        if (networkError) console.error(`[Network error]:`, networkError);
      });

      const basicLink = httpLink.create({
        uri: 'https://api-eu-central-1.graphcms.com/v2/ckn3rt81v7l6l01xr9osrbzsl/master',
      });

      return {
        link: ApolloLink.from([errorLink, basicLink]),
        cache,
      };
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        const platformId = inject(PLATFORM_ID);
        const transferState = inject(TransferState);
        const apollo = inject(Apollo);
        return () => {
          if (!isPlatformBrowser(platformId)) {
            transferState.onSerialize(APOLLO_STATE_KEY, () => {
              return apollo.client.extract();
            });
          }
        };
      },
      multi: true,
    },
  ],
};
